import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Music, Heart, Gift, Star, ArrowRight, X } from 'lucide-react';
import Header from '../components/Header';

const LandingPage: React.FC = () => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const openVideoModal = () => setIsVideoModalOpen(true);
  const closeVideoModal = () => setIsVideoModalOpen(false);

  return (
    <div className="min-h-screen bg-midnight">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="bg-midnight text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <div className="flex justify-center mb-8">
              <img 
                src="/logo.png" 
                alt="BandCam.io Logo" 
                className="w-24 h-24 object-contain"
              />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 font-display">
              Personalized Videos from
              <br />
              Your School's Band
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-300 max-w-3xl mx-auto">
              Turn life's big moments into unforgettable memories with a personalized video from your school's band. 
              Celebrate holidays, birthdays, and graduations while directly supporting student musicians.
              <h2>Support the Band and Share the Music!</h2>
            </p>
            
            {/* Band Images Showcase */}
            <div className="mb-8">
              <div className="flex justify-center items-center space-x-4 md:space-x-8">
                <div className="relative group">
                  <img 
                    src="/band1.png" 
                    alt="School Band Performance" 
                    className="w-20 h-20 md:w-24 md:h-24 border-2 border-white object-cover transform transition-transform group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300"></div>
                </div>
                <div className="relative group">
                  <img 
                    src="/band2.png" 
                    alt="School Band Performance" 
                    className="w-20 h-20 md:w-24 md:h-24 border-2 border-white object-cover transform transition-transform group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300"></div>
                </div>
                <div className="relative group">
                  <img 
                    src="/band3.png" 
                    alt="School Band Performance" 
                    className="w-20 h-20 md:w-24 md:h-24 border-2 border-white object-cover transform transition-transform group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300"></div>
                </div>
              </div>
              <p className="text-sm md:text-base text-gray-300 mt-4">
                Real bands from participating schools
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/schools"
                className="btn bg-goldie text-black hover:bg-opacity-90 text-lg px-8 py-4 border-2 border-black font-bold"
              >
                Create My Video
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
                <button 
                  className="btn bg-transparent text-white border-2 border-white hover:bg-white hover:text-black text-lg px-8 py-4"
                  onClick={openVideoModal}
                >
                  See Real Examples
                </button>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {isVideoModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-midnight border-2 border-white rounded-xl max-w-4xl w-full max-h-screen overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b-2 border-white">
              <h3 className="text-xl font-bold text-white">Example Band Video</h3>
              <button
                onClick={closeVideoModal}
                className="text-white hover:text-goldie transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6">
              <div className="aspect-video bg-black border-2 border-white rounded-xl mb-4">
                <video
                  controls
                  className="w-full h-full rounded-xl"
                  poster="/band1.png"
                >
                  <source src="/nottaBand.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              
              <div className="text-white">
                <h4 className="text-lg font-semibold mb-2">Sample Personalized Band Message</h4>
                <p className="text-gray-300 mb-4">
                  This is an example of the type of personalized video message your school's band can create 
                  for your special occasions. Each video is custom-made with your specific message and celebration details.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/schools"
                    className="btn bg-goldie text-black hover:bg-opacity-90 px-6 py-3 font-bold"
                    onClick={closeVideoModal}
                  >
                    Order Your Custom Video
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                  <button
                    onClick={closeVideoModal}
                    className="btn bg-transparent text-white border-2 border-white hover:bg-white hover:text-black px-6 py-3"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Features Section */}
      <section className="py-20 bg-sky">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4 font-display">
              How It Works
            </h2>
            <p className="text-xl text-white-700 max-w-2xl mx-auto">
              Get a personalized video message from your school's band in just a few simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-goldie rounded-lg w-16 h-16 border-2 border-black flex items-center justify-center mx-auto mb-4">
                <Music className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-semibold text-black mb-2">1. Select Your Band</h3>
              <p className=" text-white-700">
                Select your school from our list of participating bands
              </p>
            </div>

            <div className="text-center">
              <div className="bg-goldie rounded-lg w-16 h-16 border-2 border-black flex items-center justify-center mx-auto mb-4">
                <Gift className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-semibold text-black mb-2">2. Choose the Celebration</h3>
              <p className=" text-white-700">
                Choose from birthdays, graduations, holidays, and more
              </p>
            </div>

            <div className="text-center">
              <div className="bg-goldie rounded-lg w-16 h-16 border-2 border-black flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-semibold text-black mb-2">3. Get Your Personalized Video</h3>
              <p className=" text-white-700">
                Get a personalized video message delivered to your email
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Band Showcase Section */}
      <section className="py-20 bg-goldie">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4 font-display">
              Meet Our Amazing Bands
            </h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Meet the student musicians bringing your celebrations to life. 
              Each band creates custom video performances that make birthdays, graduations, and milestones truly special.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-sky border-2 border-black overflow-hidden transform transition-transform hover:scale-105">
              <div className="relative">
                <img 
                  src="/band1.png" 
                  alt="School Band 1" 
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-30"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-lg font-semibold">Austin High School Band</h3>
                  <p className="text-sm opacity-90">Marching Band Excellence</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-black mb-4">
                  "We love creating personalized messages for families. It's amazing to see the joy our videos bring!"
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white-700">Starting at $50</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 border-2 border-black text-xs font-medium bg-goldie text-black">
                    Available
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-sky border-2 border-black overflow-hidden transform transition-transform hover:scale-105">
              <div className="relative">
                <img 
                  src="/band2.png" 
                  alt="School Band 2" 
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-30"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-lg font-semibold">Westlake High Band</h3>
                  <p className="text-sm opacity-90">Concert Band Masters</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-black mb-4">
                  "Making music personal and meaningful is what we do best. Every video is a unique celebration!"
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white-700">Starting at $30</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 border-2 border-black text-xs font-medium bg-goldie text-black">
                    Available
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-sky border-2 border-black overflow-hidden transform transition-transform hover:scale-105">
              <div className="relative">
                <img 
                  src="/band3.png" 
                  alt="School Band 3" 
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-30"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-lg font-semibold">Lake Travis Band</h3>
                  <p className="text-sm opacity-90">Jazz Ensemble Stars</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-black mb-4">
                  "From jazz to classical, we create the perfect musical backdrop for your special moments."
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm  text-white-700">Starting at $40</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 border-2 border-black text-xs font-medium bg-goldie text-black">
                    Available
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/schools"
              className="btn bg-black text-white text-lg px-8 py-4 border-2 border-black hover:bg-gray-800"
            >
              View All Participating Schools
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-midnight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-display">
              What Families Are Saying
            </h2>
            <p className="text-3xl font-bold mb-12 text-center font-display">Placeholder for now</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-goldie fill-current" />
                ))}
              </div>
              <p className="text-white text-lg mb-4">
                "My daughter was so surprised and touched by her birthday video from the band. 
                It was <i>the highlight of her celebration!</i>"
              </p>
              <p className="font-semibold text-white">- Sarah M., Parent</p>
            </div>

            <div className="card">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-goldie fill-current" />
                ))}
              </div>
              <p className="text-white text-lg mb-4">
                "Perfect graduation gift! The band's message was personal and meaningful. 
                <i>Highly recommend!</i>"
              </p>
              <p className="font-semibold text-white">- Michael R., Parent</p>
            </div>

            <div className="card">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-goldie fill-current" />
                ))}
              </div>
              <p className="text-white text-lg mb-4">
                "Such a unique and special way to celebrate. The video quality was <i>excellent 
                and delivery was quick!</i>"
              </p>
              <p className="font-semibold text-white">- Jennifer L., Parent</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-midnight text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 font-display">
            Ready to Create Something Special?
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Bring the band to your celebration. Order your personalized video today and make the moment unforgettable.
          </p>
          <Link
            to="/schools"
            className="btn bg-goldie text-black hover:bg-opacity-90 text-lg px-8 py-4 border-2 border-black font-bold"
          >
            Get Started Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-midnight text-white border-t-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img 
                  src="/logo.png" 
                  alt="BandCam.io Logo" 
                  className="w-8 h-8 object-contain"
                />
                <span className="ml-2 text-xl font-bold">BandCam.io</span>
              </div>
              <p className="text-gray-300 mb-4">
                Connecting families with their school bands to create unforgettable personalized video messages for special occasions.
              </p>
              <div className="flex space-x-4">
                <Star className="w-5 h-5 text-goldie" />
                <Star className="w-5 h-5 text-goldie" />
                <Star className="w-5 h-5 text-goldie" />
                <Star className="w-5 h-5 text-goldie" />
                <Star className="w-5 h-5 text-goldie" />
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/schools" className="text-gray-300 hover:text-white">Find Your School</Link></li>
                <li><Link to="/blog" className="text-gray-300 hover:text-white">Blog</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><Link to="/help" className="text-gray-300 hover:text-white">Help Center</Link></li>
                <li><Link to="/faq" className="text-gray-300 hover:text-white">FAQ</Link></li>
                <li><Link to="/privacy" className="text-gray-300 hover:text-white">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-300">
            <p>&copy; {new Date().getFullYear()} BandCam.io. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 